import { DataProvider } from '@refinedev/core';
import { getIdToken } from './auth-provider';
import { apiErrorMessage } from '../lib/api-error';

// Create authenticated fetch wrapper
const authenticatedFetch = async (url: string, options: RequestInit = {}) => {
  const token = await getIdToken();

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // The status rides on the error: authProvider.onError ends the session on a
    // 401 (session-end.ts errorEndsTheSession) and Refine reads `statusCode`.
    // A body that is not JSON is the gateway's or the network's, not "Network error".
    const body: unknown = await response.json().catch(() => undefined);
    throw Object.assign(new Error(apiErrorMessage(body, response.status)), {
      status: response.status,
      statusCode: response.status,
    });
  }

  return response.json();
};

// Resource to API path mapping
const resourceToPath: Record<string, string> = {
  users: '/v1/admin/users',
  reports: '/v1/admin/reports',
  audit: '/v1/admin/audit',
  waitlist: '/v1/admin/waitlist',
  'beta-applications': '/v1/admin/beta-applications',
  'analytics/dashboard': '/v1/admin/analytics/dashboard',
  'analytics/users': '/v1/admin/analytics/users',
  'support/lookup': '/v1/admin/support/lookup',
  'verification/queue': '/v1/admin/verification/queue',
  'moderation/photos': '/v1/admin/moderation/photos',
  promotions: '/v1/admin/promotions',
};

// Data provider factory
export const dataProvider = (apiUrl: string): DataProvider => ({
  getList: async ({ resource, pagination, filters, sorters }) => {
    const path = resourceToPath[resource] || `/v1/admin/${resource}`;
    const url = new URL(`${apiUrl}${path}`);

    // Pagination
    if (pagination) {
      url.searchParams.set('page', String(pagination.current || 1));
      url.searchParams.set('limit', String(pagination.pageSize || 20));
    }

    // Filters
    if (filters) {
      filters.forEach((filter) => {
        if ('field' in filter && filter.value !== undefined) {
          url.searchParams.set(filter.field, String(filter.value));
        }
      });
    }

    // Sorters
    if (sorters && sorters.length > 0) {
      url.searchParams.set('sort_by', sorters[0].field);
      url.searchParams.set('sort_order', sorters[0].order);
    }

    const response = await authenticatedFetch(url.toString());

    // Handle different response structures
    if (resource === 'users') {
      return {
        data: response.users || [],
        total: response.total || 0,
      };
    }

    if (resource === 'reports') {
      return {
        data: response.reports || [],
        total: response.total || 0,
      };
    }

    if (resource === 'audit') {
      return {
        data: response.entries || [],
        total: response.total || 0,
      };
    }

    if (resource === 'waitlist') {
      return {
        data: response.entries || [],
        total: response.total || 0,
      };
    }

    if (resource === 'beta-applications') {
      return {
        data: response.entries || [],
        total: response.total || 0,
      };
    }

    if (resource === 'verification/queue') {
      return {
        data: response.data || [],
        total: response.total || 0,
      };
    }

    if (resource === 'moderation/photos') {
      return {
        data: response.data || [],
        total: response.total || 0,
      };
    }

    // Default for analytics endpoints
    return {
      data: Array.isArray(response) ? response : [response],
      total: Array.isArray(response) ? response.length : 1,
    };
  },

  getOne: async ({ resource, id }) => {
    const path = resourceToPath[resource] || `/v1/admin/${resource}`;
    const response = await authenticatedFetch(`${apiUrl}${path}/${id}`);

    return {
      data: response,
    };
  },

  create: async ({ resource, variables }) => {
    const path = resourceToPath[resource] || `/v1/admin/${resource}`;
    const response = await authenticatedFetch(`${apiUrl}${path}`, {
      method: 'POST',
      body: JSON.stringify(variables),
    });

    return {
      data: response,
    };
  },

  update: async ({ resource, id, variables }) => {
    const path = resourceToPath[resource] || `/v1/admin/${resource}`;
    const response = await authenticatedFetch(`${apiUrl}${path}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(variables),
    });

    return {
      data: response,
    };
  },

  deleteOne: async ({ resource, id, variables }) => {
    const path = resourceToPath[resource] || `/v1/admin/${resource}`;
    await authenticatedFetch(`${apiUrl}${path}/${id}`, {
      method: 'DELETE',
      body: variables ? JSON.stringify(variables) : undefined,
    });

    return {
      data: { id } as never,
    };
  },

  getApiUrl: () => apiUrl,

  // Custom method for non-CRUD operations
  custom: async ({ url, method, payload, query }) => {
    const fullUrl = new URL(`${apiUrl}${url}`);

    if (query) {
      Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined) {
          fullUrl.searchParams.set(key, String(value));
        }
      });
    }

    const options: RequestInit = {
      method: method || 'GET',
    };

    if (payload && method?.toUpperCase() !== 'GET') {
      options.body = JSON.stringify(payload);
    }

    const response = await authenticatedFetch(fullUrl.toString(), options);

    return {
      data: response,
    };
  },
});
